import React, { useState } from 'react';
import { useTeamChat } from '@/context/TeamChatContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
// Checkbox import removed
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

interface CreateGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isGlobalDarkMode: boolean;
}

const CreateGroupDialog: React.FC<CreateGroupDialogProps> = ({ open, onOpenChange, isGlobalDarkMode }) => {
  const { createChannel, users, currentUser } = useTeamChat();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);

  // Filter out current user from the list since they are automatically added as owner
  const selectableUsers = users.filter(u => u.id !== currentUser?.id);

  const toggleMember = (userId: string) => {
    setSelectedMembers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllMembers = () => {
    if (selectedMembers.length === selectableUsers.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(selectableUsers.map(u => u.id));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      try {
        await createChannel(name.trim(), description.trim() || undefined, isPrivate, selectedMembers);
        setName('');
        setDescription('');
        setIsPrivate(false);
        setSelectedMembers([]);
        onOpenChange(false);
      } catch (error) {
        console.error('Failed to create channel:', error);
      }
    }
  };

  const textColor = isGlobalDarkMode ? 'text-foreground' : 'text-gray-900';
  const subTextColor = isGlobalDarkMode ? 'text-muted-foreground' : 'text-gray-500';
  const borderColor = isGlobalDarkMode ? 'border-border' : 'border-gray-200';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("sm:max-w-md max-h-[90vh] flex flex-col", isGlobalDarkMode ? "bg-background border-border" : "")}>
        <DialogHeader>
          <DialogTitle className={textColor}>Create a group</DialogTitle>
          <DialogDescription className="sr-only">
            Create a new group chat with name, description, and members.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="space-y-4 py-4 overflow-y-auto flex-1 px-1">
            <div className="space-y-2">
              <Label htmlFor="name" className={subTextColor}>Name</Label>
              <Input
                id="name"
                placeholder="e.g. marketing"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={isGlobalDarkMode ? "bg-card border-border text-foreground placeholder:text-muted-foreground" : ""}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description" className={subTextColor}>Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="What's this group about?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={cn("resize-none", isGlobalDarkMode ? "bg-card border-border text-foreground placeholder:text-muted-foreground" : "")}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="private" className={textColor}>Make private</Label>
                <p className={cn("text-xs", subTextColor)}>
                  Only invited members can see this group
                </p>
              </div>
              <Switch
                id="private"
                checked={isPrivate}
                onCheckedChange={setIsPrivate}
                className={isGlobalDarkMode ? "data-[state=checked]:bg-workspace-accent" : ""}
              />
            </div>

            <div className="space-y-2 mt-4">
              <div className="flex items-center justify-between">
                <Label className={subTextColor}>Add Members</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={toggleAllMembers}
                  className={cn("h-auto p-0 text-xs", isGlobalDarkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-700")}
                >
                  {selectedMembers.length === selectableUsers.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className={cn("border rounded-md", borderColor)}>
                <ScrollArea className="h-[200px]">
                  <div className="p-2 space-y-1">
                    {selectableUsers.length === 0 ? (
                      <p className={cn("text-sm text-center py-4", subTextColor)}>No other members found</p>
                    ) : (
                      selectableUsers.map(user => (
                        <div
                          key={user.id}
                          className={cn(
                            "flex items-center gap-3 p-2 rounded-md transition-colors cursor-pointer",
                            isGlobalDarkMode ? "hover:bg-card" : "hover:bg-gray-50"
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            toggleMember(user.id);
                          }}
                        >
                          <div className={cn(
                            "h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center transition-colors pointer-events-none",
                            selectedMembers.includes(user.id)
                              ? "bg-workspace-accent border-workspace-accent text-white"
                              : "border-gray-400 bg-transparent",
                            isGlobalDarkMode && !selectedMembers.includes(user.id) ? "border-border" : ""
                          )}>
                            {selectedMembers.includes(user.id) && <Check className="h-3 w-3" />}
                          </div>
                          <Avatar className="w-6 h-6">
                            <AvatarImage src={user.avatar} />
                            <AvatarFallback className="text-[10px]">{user.displayName.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <span className={cn("text-sm truncate", textColor)}>{user.displayName}</span>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>

          </div>
          <DialogFooter className="gap-2 sm:gap-0 mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className={cn("transition-colors", isGlobalDarkMode ? "border-border hover:bg-card text-muted-foreground" : "border-gray-200 hover:bg-gray-100 text-gray-600")}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim()}
              className="bg-workspace-accent hover:bg-workspace-accent-hover text-white"
            >
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateGroupDialog;
